import { useNotification } from "@refinedev/core";
import { Loader2, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { notifyMutationError } from "@/lib/repository-utils";
import { Sentry } from "@/lib/sentry";
import type { EnrichmentEntity } from "@/lib/tina/content-types";
import type { EnrichmentMode } from "@/lib/tina/enrichment-jobs";
import { cn } from "@/lib/utils";
import { useEnrichmentIntake } from "@/pages/tina/hooks/use-enrichment-intake";

/**
 * A per-entity "Re-enrich" button on `POST /enrichments`.
 *
 * **Where this is wired, and where it deliberately is not.** Artist, venue,
 * geodata and event already carry a per-entity enrich trigger that reaches Tina
 * through the backend's forwarder, and those are deliberately **not** re-pointed, so
 * this button is not stacked next to them — two buttons that queue the same job
 * through two paths is worse than one. It goes on the entities that had no
 * affordance at all: genre, movie, person, show-genre and screening, three of
 * which have no edit page either.
 *
 * The one thing it does that the backend route cannot is report
 * `already_queued`, which is the whole reason the intake exists alongside the
 * old buttons rather than replacing them.
 */
export function ReEnrichButton({
  entityType,
  entityId,
  mode,
  label = "Re-enrich",
  size = "sm",
  variant = "outline",
  className,
  onQueued,
}: {
  entityType: EnrichmentEntity;
  entityId: string | null | undefined;
  /** Defaults to Tina's own default, `both` (enrich, then embed if embeddable). */
  mode?: EnrichmentMode;
  label?: string;
  size?: "sm" | "default";
  variant?: "outline" | "default" | "secondary";
  className?: string;
  /** Fired after a response, queued or debounced — a good place to refetch. */
  onQueued?: () => void;
}) {
  const { enqueue, isPending } = useEnrichmentIntake();
  const { open } = useNotification();

  if (!entityId) return null;

  async function queue() {
    try {
      const response = await enqueue({
        entity_type: entityType,
        entity_id: entityId as string,
        ...(mode ? { mode } : {}),
      });
      if (response) onQueued?.();
    } catch (error) {
      // `enqueue` reports its own HTTP failures and answers `null`; the only
      // thing that escapes it is the synchronous `mode`/entity guard, which
      // fires *before* the request and so never reaches that reporting. It is
      // a mis-wired button rather than a request that failed — and unhandled it
      // gave the curator a button that visibly did nothing, and Sentry a bare
      // unhandled rejection with no breadcrumb saying which entity.
      Sentry.captureException(error);
      notifyMutationError(open, "Queue enrichment", error);
    }
  }

  return (
    <Button
      type="button"
      size={size}
      variant={variant}
      className={cn("gap-1.5", className)}
      disabled={isPending}
      onClick={(event) => {
        event.stopPropagation();
        void queue();
      }}
    >
      {isPending ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Sparkles className="h-3.5 w-3.5" />
      )}
      {label}
    </Button>
  );
}
